import { cva, type VariantProps } from "class-variance-authority";
import clsx from "clsx";
import {
  type ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import TextareaAutosize, {
  type TextareaAutosizeProps,
} from "react-textarea-autosize";

const textAreaVariants = cva(
  [
    "relative resize-none rounded-3xl border border-black-100 bg-black-100 text-black-950 outline-2 transition-[background,border] scrollbar-thin",
    "placeholder:text-black-500",
    "hover:border-black-200 hover:bg-black-200",
    "focus-visible:border-purple-400 focus-visible:bg-white focus-visible:outline-none focus-visible:outline-offset-0",
    "disabled:cursor-not-allowed disabled:text-black-400 disabled:placeholder:text-black-400 disabled:hover:border-black-100 disabled:hover:bg-black-100",
  ],
  {
    variants: {
      variant: {
        sm: "rounded-xl p-2 text-xs-medium",
        md: "rounded-2xl p-2.5 text-sm-medium",
        lg: "rounded-3xl p-3 text-md-medium",
      },
    },
    defaultVariants: {
      variant: "md",
    },
  },
);

interface TextAreaProps
  extends TextareaAutosizeProps,
    VariantProps<typeof textAreaVariants> {
  hasError?: boolean;
}

function TextArea(
  { hasError = null, variant, className, ...props }: TextAreaProps,
  ref: ForwardedRef<HTMLTextAreaElement>,
) {
  // Create a ref for the textarea
  const localRef = useRef<HTMLTextAreaElement>(null);

  // Sync the forwarded ref with the local ref
  useImperativeHandle(ref, () => localRef.current);

  return (
    <TextareaAutosize
      className={clsx(
        textAreaVariants({ variant, className }),
        hasError && "!border-red-400 focus-visible:outline-red-300",
      )}
      ref={localRef}
      {...props}
    />
  );
}
TextArea.displayName = "TextArea";

export default forwardRef(TextArea);
